import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Divider,
  Badge,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  MedicalServices as MedicalServicesIcon,
  LocalHospital as HospitalIcon,
  Receipt as ReceiptIcon,
  Notifications as NotificationsIcon,
  AccountCircle as AccountCircleIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Business as BusinessIcon,
  Medication as MedicationIcon,
} from '@mui/icons-material';
import { useNavigation } from '../contexts/NavigationContext';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';
import TenantSelector from '../components/TenantSelector';
import CareXLogo from '../components/CareXLogo';
import NotificationDropdown from '../components/NotificationDropdown';
import { Link, useLocation, useNavigate } from 'react-router-dom';

interface AppLayoutProps {
  children: React.ReactNode;
}

const drawerWidth = 280;
const collapsedWidth = 70;

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [notifAnchorEl, setNotifAnchorEl] = useState<null | HTMLElement>(null);
  const notifOpen = Boolean(notifAnchorEl);
  const { unreadNotifications } = useNavigation();
  const { tenant } = useTenant();
  const location = useLocation();

  const { user, logout } = useAuth();
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const isClientAdmin = user?.role === 'CLIENT_ADMIN';

  // Determine which menu items to show based on user role
  let menuItems: Array<{ text: string; icon: React.ReactNode; path: string }> = [];
  let settingsItems: Array<{ text: string; icon: React.ReactNode; path: string }> = [];

  if (isSuperAdmin) {
    // Super admin menu
    menuItems = [
      { text: 'Dashboard', icon: <DashboardIcon />, path: '/super-admin/dashboard' },
      { text: 'Client Management', icon: <PeopleIcon />, path: '/super-admin/clients' },
      { text: 'Facility Management', icon: <HospitalIcon />, path: '/tenant-management' },
      { text: 'Users', icon: <PeopleIcon />, path: '/admin/users' },
    ];
    settingsItems = [];
  } else if (isClientAdmin) {
    // Client admin menu (manages multiple facilities)
    menuItems = [
      { text: 'Dashboard', icon: <DashboardIcon />, path: '/client/dashboard' },
      { text: 'My Facilities', icon: <BusinessIcon />, path: '/tenant-management' },
      { text: 'Admissions', icon: <HospitalIcon />, path: '/admissions' },
      { text: 'Residents', icon: <PeopleIcon />, path: '/residents' },
      // { text: 'Patient Diagnosis', icon: <AssignmentIcon />, path: '/patient-diagnosis' },
      { text: 'Users', icon: <PeopleIcon />, path: '/admin/users' },
    ];
    settingsItems = [];
  } else {
    // Regular facility user menu
    menuItems = [
      { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
      { text: 'Admissions', icon: <HospitalIcon />, path: '/admissions' },
      { text: 'Residents', icon: <PeopleIcon />, path: '/residents' },
      { text: 'Medication Orders', icon: <MedicationIcon />, path: '/medication-orders' },

      // { text: 'Patient Diagnosis', icon: <AssignmentIcon />, path: '/patient-diagnosis' },
    ];
  }

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setSidebarCollapsed(!sidebarCollapsed);
    }
  };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'linear-gradient(135deg, #2CA6A4 0%, #174A5A 100%)',
          color: 'white',
          p: sidebarCollapsed ? 1 : 2,
          minHeight: 70,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
          <CareXLogo style={{ height: '32px', marginRight: sidebarCollapsed ? 0 : 8 }} />
          <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 'bold', fontSize: '1.1rem', flex: 1 }}>
            {tenant?.name || 'Healthcare'}
          </Typography>
        </Box>
        {!isMobile && (
          <Tooltip title={sidebarCollapsed ? 'Expand' : 'Collapse'}>
            <IconButton
              onClick={handleDrawerToggle}
              sx={{ color: 'white', ml: 1 }}
              size="small"
            >
              {sidebarCollapsed ? <MenuIcon /> : <ChevronLeftIcon />}
            </IconButton>
          </Tooltip>
        )}
        {isMobile && (
          <IconButton
            onClick={() => setMobileOpen(false)}
            sx={{ color: 'white' }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      {/* Tenant Selector */}
      {!sidebarCollapsed && (
        <>
          <Box sx={{ p: 1 }}>
            <TenantSelector />
          </Box>
          <Divider />
        </>
      )}

      {/* Menu Items */}
      <List sx={{ flex: 1, overflow: 'auto', p: sidebarCollapsed ? 0.5 : 1 }}>
        {menuItems.map((item) => (
          <Tooltip key={item.text} title={sidebarCollapsed ? item.text : ''} placement="right">
            <ListItem
              component={Link}
              to={item.path}
              onClick={() => isMobile && setMobileOpen(false)}
              sx={{
                backgroundColor: location.pathname === item.path
                  ? 'rgba(44, 166, 164, 0.15)'
                  : 'transparent',
                borderLeft: location.pathname === item.path
                  ? `4px solid ${theme.palette.primary.main}`
                  : 'none',
                color: location.pathname === item.path
                  ? theme.palette.primary.main
                  : theme.palette.text.primary,
                borderRadius: sidebarCollapsed ? '8px' : '0px 8px 8px 0px',
                mx: sidebarCollapsed ? 0.5 : 0,
                my: 0.5,
                px: sidebarCollapsed ? 1.5 : 2,
                py: 1.5,
                transition: 'all 0.3s ease',
                justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                '&:hover': {
                  backgroundColor: 'rgba(44, 166, 164, 0.1)',
                  transform: 'translateX(4px)',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: location.pathname === item.path
                    ? theme.palette.primary.main
                    : theme.palette.text.secondary,
                  minWidth: sidebarCollapsed ? 'auto' : 40,
                  mr: sidebarCollapsed ? 0 : 2,
                  justifyContent: 'center',
                }}
              >
                {item.icon}
              </ListItemIcon>
              {!sidebarCollapsed && (
                <ListItemText
                  primary={item.text}
                  sx={{
                    fontWeight: location.pathname === item.path ? 600 : 500,
                    '& .MuiTypography-root': {
                      fontSize: '0.95rem',
                    },
                  }}
                />
              )}
            </ListItem>
          </Tooltip>
        ))}
      </List>

      {/* Settings Section */}
      {settingsItems.length > 0 && (
        <>
          <Divider />
          <Box sx={{ p: sidebarCollapsed ? 0.5 : 1 }}>
            {!sidebarCollapsed && (
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 'bold',
                  color: 'text.secondary',
                  px: 2,
                  display: 'block',
                  mb: 1,
                  fontSize: '0.75rem',
                }}
              >
                SETTINGS
              </Typography>
            )}
            <List sx={{ p: 0 }}>
              {settingsItems.map((item) => (
                <Tooltip key={item.text} title={sidebarCollapsed ? item.text : ''} placement="right">
                  <ListItem
                    component={Link}
                    to={item.path}
                    onClick={() => isMobile && setMobileOpen(false)}
                    sx={{
                      backgroundColor: location.pathname === item.path
                        ? 'rgba(44, 166, 164, 0.15)'
                        : 'transparent',
                      borderLeft: location.pathname === item.path
                        ? `4px solid ${theme.palette.primary.main}`
                        : 'none',
                      color: location.pathname === item.path
                        ? theme.palette.primary.main
                        : theme.palette.text.primary,
                      borderRadius: sidebarCollapsed ? '8px' : '0px 8px 8px 0px',
                      mx: sidebarCollapsed ? 0.5 : 0,
                      my: 0.5,
                      px: sidebarCollapsed ? 1.5 : 2,
                      py: 1.5,
                      transition: 'all 0.3s ease',
                      justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                      '&:hover': {
                        backgroundColor: 'rgba(44, 166, 164, 0.1)',
                        transform: 'translateX(4px)',
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: location.pathname === item.path
                          ? theme.palette.primary.main
                          : theme.palette.text.secondary,
                        minWidth: sidebarCollapsed ? 'auto' : 40,
                        mr: sidebarCollapsed ? 0 : 2,
                        justifyContent: 'center',
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    {!sidebarCollapsed && (
                      <ListItemText
                        primary={item.text}
                        sx={{
                          fontWeight: location.pathname === item.path ? 600 : 500,
                          '& .MuiTypography-root': {
                            fontSize: '0.95rem',
                          },
                        }}
                      />
                    )}
                  </ListItem>
                </Tooltip>
              ))}
            </List>
          </Box>
        </>
      )}
    </Box>
  );

  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    localStorage.removeItem('currentTenantId');
    localStorage.removeItem('user');
    navigate('/login');
    handleProfileMenuClose();
  };

  const handleProfileClick = () => {
    if (user?.role === 'SUPER_ADMIN') {
      navigate('/Profile');
    } else {
      navigate('/Profile');
    }
    handleProfileMenuClose();
  };

  const currentDrawerWidth = isMobile ? drawerWidth : (sidebarCollapsed ? collapsedWidth : drawerWidth);

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { xs: '100%', md: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { xs: 0, md: `${currentDrawerWidth}px` },
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
          transition: 'width 0.3s ease, margin-left 0.3s ease',
        }}
      >
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
          {/* Left side - Menu toggle */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                onClick={handleDrawerToggle}
                sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(44, 166, 164, 0.1)',
                  },
                }}
              >
                <MenuIcon />
              </IconButton>
            )}
            {!isMobile && !sidebarCollapsed && (
              <CareXLogo style={{ height: '48px', marginRight: 12 }} />
            )}
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{
                fontWeight: 'bold',
                background: 'linear-gradient(135deg, #2CA6A4 0%, #174A5A 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              {/* {tenant?.name || 'Healthcare Platform'} */}
            </Typography>
          </Box>

          {/* Right side - Notifications and User profile */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Notifications">
              <IconButton
                size="large"
                aria-label="notifications"
                color="inherit"
                onClick={(e) => setNotifAnchorEl(e.currentTarget)}
                sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(44, 166, 164, 0.1)',
                  },
                }}
              >
                <Badge badgeContent={unreadNotifications} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={notifAnchorEl}
              open={notifOpen}
              onClose={() => setNotifAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{ sx: { mt: 1, boxShadow: 3, borderRadius: 2, minWidth: 320, maxHeight: 350, p: 0 } }}
            >
              <NotificationDropdown />
            </Menu>
            <Tooltip title="Account">
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleProfileMenuOpen}
                color="inherit"
                sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(44, 166, 164, 0.1)',
                  },
                }}
              >
                <AccountCircleIcon />
              </IconButton>
            </Tooltip>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={open}
              onClose={handleProfileMenuClose}
            >
              <MenuItem onClick={handleProfileClick}>
                <ListItemIcon>
                  <AccountCircleIcon fontSize="small" />
                </ListItemIcon>
                Profile
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Sidebar Navigation */}
      <Box
        component="nav"
        sx={{
          width: { xs: drawerWidth, md: currentDrawerWidth },
          flexShrink: { md: 0 },
          transition: 'width 0.3s ease',
        }}
        aria-label="navigation"
      >
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : true}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: isMobile ? drawerWidth : currentDrawerWidth,
              borderRight: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper,
              transition: 'width 0.3s ease',
              overflowX: 'hidden',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { xs: '100%', md: `calc(100% - ${currentDrawerWidth}px)` },
          minHeight: '100vh',
          backgroundColor: theme.palette.background.default,
          transition: 'width 0.3s ease',
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default AppLayout;